import { Button, EditIcon, Page, PersonRemoveOutlined } from '@occc-app/design-system';
import { RetailerNetworkUserFragment, Retailer_Network_User_Role } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  UpdateOpenCommerceUser,
  RemoveUserFromRetailerNetwork,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { RetailerNetworkUserDetailsLocationsAndLocationGroups } from './retailer-network-user-details-locations-and-groups/retailer-network-user-details-locations-and-groups.component';
import { RetailerNetworkUserUpdateDialog } from './components/retailer-network-user-update-dialog/retailer-network-user-update-dialog.component';
import { RetailerNetworkUserUpdateFormValues } from './components/retailer-network-user-update-dialog/retailer-network-user-update-dialog.types';
import { RetailerNetworkUserDetailsSection } from './retailer-network-user-details-section.component';
import { useGetIsUserPermittedToRemoveUserFromNetwork } from './hooks/use-get-is-user-permitted-to-remove-from-network.hook';

import { RemoveUserFromRetailerNetworkDialog } from '../components/remove-user-from-retailer-network-dialog.component';

type RetailerNetworkUserDetailsPageProps = {
  retailerNetworkUuid: string;
  retailerNetworkUser: RetailerNetworkUserFragment;
  isOpenCommerceUserUpdateDialogOpen: boolean;
  removeUserFromRetailerNetworkDialogOpen: boolean;
  isRemoveUserFromRetailerNetworkInProgress: boolean;
  handleRemoveUserFromRetailerNetwork: () => void;
  handleOpenCommerceUserUpdateDialogOpen: () => void;
  setRemoveUserFromRetailerNetworkDialogOpen: (isShowDialog: boolean) => void;
  onRemoveUserFromRetailerNetworkConfirmPress: () => void;
  handleOpenCommerceUserUpdate: (values: RetailerNetworkUserUpdateFormValues) => Promise<void>;
};

export const RetailerNetworkUserDetailsPage = ({
  retailerNetworkUser,
  retailerNetworkUuid,
  isOpenCommerceUserUpdateDialogOpen,
  isRemoveUserFromRetailerNetworkInProgress,
  removeUserFromRetailerNetworkDialogOpen,
  handleOpenCommerceUserUpdateDialogOpen,
  handleOpenCommerceUserUpdate,
  handleRemoveUserFromRetailerNetwork,
  setRemoveUserFromRetailerNetworkDialogOpen,
  onRemoveUserFromRetailerNetworkConfirmPress,
}: RetailerNetworkUserDetailsPageProps) => {
  const isTargetUserSiteAdminOrUser =
    retailerNetworkUser.role === Retailer_Network_User_Role.SiteAdmin ||
    retailerNetworkUser.role === Retailer_Network_User_Role.SiteUser;

  const isRemoveFromNetworkButtonVisible = useGetIsUserPermittedToRemoveUserFromNetwork(
    isTargetUserSiteAdminOrUser,
  );

  return (
    <Page
      browserTabTitle='Retailer Network User Details'
      pageHeaderProps={{ title: retailerNetworkUser.name }}
      containerProps={{ variant: 'md' }}
      actions={
        <>
          <Can I={RemoveUserFromRetailerNetwork({ uuid: retailerNetworkUuid })}>
            {/* After BE will add ability to check roles in policy, we can remove this condition */}
            {isRemoveFromNetworkButtonVisible && (
              <Button
                variant='contained'
                color='error'
                startIcon={<PersonRemoveOutlined />}
                onClick={handleRemoveUserFromRetailerNetwork}
              >
                Remove From Network
              </Button>
            )}
          </Can>
          <Can I={UpdateOpenCommerceUser()}>
            <Button
              variant='contained'
              color='secondary'
              startIcon={<EditIcon />}
              onClick={handleOpenCommerceUserUpdateDialogOpen}
            >
              Edit
            </Button>
          </Can>
        </>
      }
    >
      <RetailerNetworkUserDetailsSection retailerNetworkUser={retailerNetworkUser} />

      {isTargetUserSiteAdminOrUser && (
        <RetailerNetworkUserDetailsLocationsAndLocationGroups
          retailerNetworkUuid={retailerNetworkUuid}
          retailerNetworkUserUuid={retailerNetworkUser.uuid}
          retailerNetworkUserRole={retailerNetworkUser.role}
          openCommerceUserUuid={retailerNetworkUser.userUuid}
        />
      )}

      <RetailerNetworkUserUpdateDialog
        retailerNetworkUser={retailerNetworkUser}
        open={isOpenCommerceUserUpdateDialogOpen}
        onClose={handleOpenCommerceUserUpdateDialogOpen}
        onSubmit={handleOpenCommerceUserUpdate}
      />

      <RemoveUserFromRetailerNetworkDialog
        open={removeUserFromRetailerNetworkDialogOpen}
        setOpen={setRemoveUserFromRetailerNetworkDialogOpen}
        onConfirm={onRemoveUserFromRetailerNetworkConfirmPress}
        isRemoveUserFromRetailerNetworkInProgress={isRemoveUserFromRetailerNetworkInProgress}
      />
    </Page>
  );
};
